import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Heart, Loader2, Users, PauseCircle, PlayCircle, CheckCircle2, XCircle, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, useAuth } from "../lib/auth";
import BtcLogo from "../components/BtcLogo";

export default function BuddyDashboard() {
  const { user, loading } = useAuth();
  const [buddy, setBuddy] = useState(undefined);
  const [connections, setConnections] = useState([]);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const [{ data: me }, { data: conns }] = await Promise.all([
        api.get("/buddy/me"),
        api.get("/buddy/connections"),
      ]);
      setBuddy(me.buddy);
      setConnections(conns.items || []);
    } catch (_e) {
      setBuddy(null);
    }
  };

  useEffect(() => { if (user) refresh(); }, [user]);

  const incoming = useMemo(() => connections.filter((c) => c.viewer_role === "buddy" && c.status === "requested"), [connections]);
  const active = useMemo(() => connections.filter((c) => c.viewer_role === "buddy" && c.status === "accepted"), [connections]);
  const past = useMemo(() => connections.filter((c) => c.viewer_role === "buddy" && ["declined", "ended"].includes(c.status)), [connections]);

  if (loading || buddy === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="w-8 h-8 text-[#23803F] animate-spin" />
      </div>
    );
  }
  if (!user || typeof user !== "object") return <Navigate to="/login" replace />;
  if (!buddy) {
    return (
      <div className="min-h-screen bg-[#F8F9FB] flex items-center justify-center p-6">
        <div className="max-w-lg bg-white rounded-3xl p-10 border border-gray-100 shadow-sm text-center">
          <h1 className="text-2xl font-bold text-[#0B132B]">You're not a Buddy yet</h1>
          <p className="text-[#4B5563] mt-2">Apply to become a BTC Buddy and support people who need someone who's been there.</p>
          <Link to="/buddy/apply" className="btc-btn-primary mt-6 inline-flex" data-testid="buddy-dash-apply">
            Apply to be a Buddy
          </Link>
        </div>
      </div>
    );
  }

  const togglePause = async () => {
    setBusy(true);
    try {
      const target = buddy.status === "active" ? "paused" : "active";
      const { data } = await api.patch("/buddy/me", { status: target });
      setBuddy({ ...buddy, ...data.buddy });
      toast.success(target === "paused" ? "Paused — you won't appear in new matches." : "You're active again.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed.");
    } finally {
      setBusy(false);
    }
  };

  const respond = async (id, action) => {
    try {
      await api.patch(`/buddy/connections/${id}`, { action });
      toast.success(action === "accept" ? "Connection accepted." : action === "decline" ? "Request declined." : "Ended.");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed.");
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FB]" data-testid="buddy-dashboard-page">
      <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        <div className="flex items-center justify-between mb-6">
          <Link to="/portal/dashboard"><BtcLogo className="h-11 w-auto" /></Link>
          <button
            data-testid="buddy-pause-toggle"
            onClick={togglePause}
            disabled={busy}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold ${
              buddy.status === "active"
                ? "bg-orange-100 text-orange-700 hover:bg-orange-200"
                : "bg-[#2D9B4E] text-white hover:bg-[#2D9B4E]/90"
            }`}
          >
            {buddy.status === "active" ? (<><PauseCircle className="w-4 h-4" /> Pause matching</>) : (<><PlayCircle className="w-4 h-4" /> Go active</>)}
          </button>
        </div>

        <div className="bg-white rounded-3xl p-6 sm:p-8 border border-gray-100 shadow-sm mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[#2D9B4E] text-white flex items-center justify-center">
              <Heart className="w-6 h-6" fill="white" />
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-[#4B5563]">
                Buddy · {buddy.journey_type}
              </div>
              <h1 className="text-2xl font-extrabold text-[#0B132B]">
                {user.name || user.email}
              </h1>
            </div>
            <div className="ml-auto">
              <StatusPill status={buddy.status} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
            <Stat label="Active connections" value={`${buddy.active_connection_count || 0} / ${buddy.capacity || 3}`} icon={Users} />
            <Stat label="Languages" value={(buddy.languages || []).join(", ")} />
            <Stat label="Location" value={buddy.location} />
          </div>
          <p className="text-[#4B5563] mt-6 whitespace-pre-line">{buddy.bio}</p>
        </div>

        <Section title="New requests" count={incoming.length} testid="buddy-incoming-list">
          {incoming.length === 0 ? <Empty label="No new requests right now." /> : (
            <div className="space-y-3">
              {incoming.map((c) => (
                <div key={c.id} className="bg-white rounded-2xl p-5 border border-gray-100 flex flex-wrap items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-[#0B132B]">{c.member_name || "Anonymous member"}</div>
                    <div className="text-xs text-[#4B5563] mt-0.5">
                      Match score: <span className="font-semibold text-[#23803F]">{c.match_score}</span>
                    </div>
                    {c.message && (
                      <div className="text-sm text-[#0B132B] mt-3 p-3 rounded-xl bg-[#F8F9FB]">
                        <MessageCircle className="w-3.5 h-3.5 inline mr-1.5 text-[#4B5563]" />
                        {c.message}
                      </div>
                    )}
                    {c.match_reasons && c.match_reasons.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {c.match_reasons.map((r, i) => (
                          <span key={i} className="text-xs px-2 py-1 rounded-full bg-[#2D9B4E]/10 text-[#23803F]">{r}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button data-testid={`buddy-decline-${c.id}`} onClick={() => respond(c.id, "decline")} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm text-[#4B5563] border border-gray-200 hover:bg-gray-50">
                      <XCircle className="w-4 h-4" /> Decline
                    </button>
                    <button data-testid={`buddy-accept-${c.id}`} onClick={() => respond(c.id, "accept")} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm bg-[#2D9B4E] text-white hover:bg-[#2D9B4E]/90">
                      <CheckCircle2 className="w-4 h-4" /> Accept
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section title="Active connections" count={active.length} testid="buddy-active-list">
          {active.length === 0 ? <Empty label="No active connections yet." /> : (
            <div className="space-y-3">
              {active.map((c) => (
                <div key={c.id} className="bg-white rounded-2xl p-5 border border-gray-100 flex items-center gap-4">
                  <div className="flex-1">
                    <div className="font-bold text-[#0B132B]">{c.member_name || "Member"}</div>
                    <div className="text-xs text-[#4B5563]">Accepted {new Date(c.responded_at || c.updated_at).toLocaleDateString()}</div>
                  </div>
                  <button data-testid={`buddy-end-${c.id}`} onClick={() => respond(c.id, "end")} className="text-sm text-red-600 hover:underline">End</button>
                </div>
              ))}
            </div>
          )}
        </Section>

        {past.length > 0 && (
          <Section title="Past" count={past.length} testid="buddy-past-list">
            <div className="space-y-2">
              {past.map((c) => (
                <div key={c.id} className="bg-white/60 rounded-xl p-4 border border-gray-100 flex items-center gap-3 text-sm">
                  <span className="text-[#0B132B]">{c.member_name || "Member"}</span>
                  <span className="ml-auto text-xs text-[#4B5563] uppercase tracking-wider">{c.status}</span>
                </div>
              ))}
            </div>
          </Section>
        )}
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    active: ["bg-[#2D9B4E]/10 text-[#23803F]", "Active"],
    paused: ["bg-orange-100 text-orange-700", "Paused"],
    pending_review: ["bg-gray-100 text-gray-700", "In review"],
    rejected: ["bg-red-100 text-red-700", "Not active"],
  };
  const [cls, label] = map[status] || ["bg-gray-100 text-gray-700", status];
  return <span data-testid="buddy-status-pill" className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${cls}`}>{label}</span>;
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="p-4 rounded-2xl bg-[#F8F9FB]">
      <div className="text-xs uppercase tracking-wider text-[#4B5563] flex items-center gap-1.5">
        {Icon && <Icon className="w-3.5 h-3.5" />} {label}
      </div>
      <div className="text-lg font-bold text-[#0B132B] mt-1 truncate">{value || "—"}</div>
    </div>
  );
}

function Section({ title, count, testid, children }) {
  return (
    <div className="mb-8" data-testid={testid}>
      <div className="flex items-center gap-3 mb-3">
        <h2 className="text-lg font-bold text-[#0B132B]">{title}</h2>
        {count > 0 && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[#2D9B4E] text-white">{count}</span>}
      </div>
      {children}
    </div>
  );
}

function Empty({ label }) {
  return <div className="bg-white/50 rounded-2xl p-6 text-center text-sm text-[#4B5563]">{label}</div>;
}
